import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  HandCoinsIcon,
  LoadsNavIcon,
  type IconProps,
} from '@hero/web-icons';

type BenefitKey = 'cashflow' | 'more_jobs' | 'competition' | 'no_contracts';

const BENEFITS: Array<{ key: BenefitKey; Icon: ComponentType<IconProps> }> = [
  { key: 'cashflow', Icon: HandCoinsIcon },
  { key: 'more_jobs', Icon: LoadsNavIcon },
  { key: 'competition', Icon: CheckIcon },
  { key: 'no_contracts', Icon: CheckIcon },
];

export type FinancingBenefitsProps = {
  /** Optional title override; defaults to `landing.benefits.title`. */
  title?: string;
  className?: string;
};

/**
 * Four benefit tiles reinforcing the value prop. Grid collapses to
 * 2-col on small screens, 4-col on `md+` for clean above-the-fold
 * scanning.
 */
export function FinancingBenefits({ title, className }: FinancingBenefitsProps) {
  const { t } = useTranslation('m-financing');

  return (
    <section className={className}>
      <h2 className="mb-5 text-xl font-semibold text-gray-900 sm:text-2xl">
        {title ?? t('landing.benefits.title')}
      </h2>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        {BENEFITS.map(({ key, Icon }) => (
          <article
            key={key}
            className="rounded-2xl border border-gray-200 bg-white p-4 transition-colors hover:border-emerald-200 hover:bg-emerald-50/30">
            <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100">
              <Icon className="size-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">
              {t(`landing.benefits.items.${key}.title`)}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              {t(`landing.benefits.items.${key}.description`)}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
